/**
 * CommercialHeatPumpCalculatorPage — 1:1 port of
 * `docs/calc-references/commercial-heat-pump.html` (V31 reference).
 *
 * Task 3.5 of the May 2026 calculator exact-match replacement sprint
 * (`docs/superpowers/plans/2026-05-15-calculator-exact-match-replacement.md`).
 *
 * Replaces the previous CalcWizard-based shell + `CommercialHeatPump/` sub-
 * folder with a single-page layout that mirrors the customer-facing widget:
 *
 *   1. Property basics card — Climate Zone (6 zones, city hints) + Property
 *      Type (8 types). EVI banner appears below when climate === extreme-cold;
 *      Hospital banner appears below when property === hospital.
 *   2. Construction layout card — Construction Type (single / block-wise /
 *      independent / cluster). When distributed: reveals No. of blocks input
 *      + a yellow "Distributed sizing" info banner.
 *   3. Capacity card — TWO LAYOUTS based on construction:
 *        - Single building: per-property-flavour fields (rooms/occupants for
 *          hotel-class, beds for hospital, persons for barracks, rooms+
 *          occupants for hostel/budget-pg), plus kitchen + tubs.
 *        - Distributed: Distribute quick-fill row (totals + button that floor-
 *          divides across blocks) + per-block grid of field cards.
 *   4. Usage & delivery card — Usage Intensity (low / moderate / high) + Reverse
 *      Line (yes / no).
 *   5. Action bar — Calculate & get recommendation + Reset.
 *   6. Result panel:
 *        - KpiStrip (LPD / kW / climate COP / series Domestic/Commercial/Mixed/EVI)
 *        - Per-block-or-single recommendation cards (Budget + Premium side-by-side)
 *        - Engineering notes bulleted list
 *   7. ROI section (collapsed, toggle button): RoiPanel with 4-way TCO comparison.
 *   8. Lead form + Save Quotation + Convert to Proposal CTAs.
 *
 * Wire format: climate uses HYPHEN (`extreme-cold`); construction collapses
 * the 4 UI values (single | block-wise | independent | cluster) to the
 * backend's `single | blockwise` enum — `single` stays, the other 3 map to
 * `blockwise`. The UI labels track which "distributed" flavour the user
 * picked so banner copy / heading copy / "block vs cluster" labels stay
 * correct on the screen, but the backend doesn't differentiate.
 *
 * API wiring uses `commercialHpApi.calculate`/`saveQuotation` from
 * `@/api/calculators`. The route returns `roi` inline on the /sizing
 * response, so the page does NOT post to /roi separately — it just adapts
 * the legacy compound-style `RoiComparison` into the RoiPanel `RoiData`
 * shape (4 tech cards + cumulative series + notes).
 *
 * The meeting-session gate is preserved (the route requires a meeting
 * session token).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Printer, Send } from 'lucide-react';
import { commercialHpApi, proposalsApi } from '@/api/calculators';
import type {
  CommercialHpInput,
  CommercialHpResult,
  CommercialHpBlock,
  RoiComparison,
} from '@/api/calculators/types';
import { KpiStrip } from '@/components/calc/KpiStrip';
import { RoiPanel, type RoiData } from '@/components/calc/RoiPanel';
import { readStoredSession, writeStoredSession } from './MeetingModeLauncher';
import { ConvertToProposalButton } from './_shared/ConvertToProposalButton';

// ─── Reference constants (verbatim from commercial-heat-pump.html) ───────────

const HEATING_WINDOW_HOURS = 8;

type ClimateKey =
  | 'hot-dry'
  | 'warm-humid'
  | 'composite'
  | 'temperate'
  | 'cold'
  | 'extreme-cold';

type PropertyKey =
  | 'hotel'
  | 'hospital'
  | 'resort'
  | 'wellness'
  | 'guesthouse'
  | 'hostel'
  | 'budget-pg'
  | 'barracks';

type ConstructionUiKey = 'single' | 'block-wise' | 'independent' | 'cluster';
type Usage = 'low' | 'moderate' | 'high';
type YN = 'yes' | 'no';

interface PropertyMeta {
  label: string;
  cityHint?: string;
  premium: boolean;
  bedBased: boolean;
  personBased: boolean;
  hospital: boolean;
}

const PROPERTY_META: Record<PropertyKey, PropertyMeta> = {
  hotel: { label: 'Hotel', premium: true, bedBased: false, personBased: false, hospital: false },
  hospital: { label: 'Hospital', premium: true, bedBased: false, personBased: false, hospital: true },
  resort: { label: 'Resort', premium: true, bedBased: false, personBased: false, hospital: false },
  wellness: { label: 'Wellness retreat', premium: true, bedBased: false, personBased: false, hospital: false },
  guesthouse: { label: 'Guest house', premium: false, bedBased: false, personBased: false, hospital: false },
  hostel: { label: 'Hostel', premium: false, bedBased: true, personBased: false, hospital: false },
  'budget-pg': { label: 'Budget hostel / PG', premium: false, bedBased: true, personBased: false, hospital: false },
  barracks: { label: 'Barrack / Dorms', premium: false, bedBased: false, personBased: true, hospital: false },
};

interface ClimateMeta {
  label: string;
  cityHint: string;
}

const CLIMATE_META: Record<ClimateKey, ClimateMeta> = {
  'hot-dry': { label: 'Hot & Dry', cityHint: 'Ahmedabad, Jaipur, Nagpur' },
  'warm-humid': { label: 'Warm & Humid', cityHint: 'Mumbai, Chennai, Goa' },
  composite: { label: 'Composite', cityHint: 'Delhi NCR, Lucknow, Bhopal' },
  temperate: { label: 'Temperate', cityHint: 'Bangalore, Pune, Hyderabad' },
  cold: { label: 'Cold', cityHint: 'Chandigarh, Shimla, Dehradun' },
  'extreme-cold': { label: 'Extreme Cold', cityHint: 'Leh, Manali, Srinagar' },
};

// Per reference HTML lines 350-358 — used to populate the ROI panel's HP COP.
const SITE_COP_BY_CLIMATE: Record<ClimateKey, number> = {
  'hot-dry': 4.2,
  'warm-humid': 4.0,
  composite: 3.8,
  temperate: 4.0,
  cold: 3.2,
  'extreme-cold': 3.4,
};

// ─── Per-block input shape (page-side only — the wire shape adds the
// occupants_per_room / beds / persons fields the backend's blockSchema accepts).
// ─────────────────────────────────────────────────────────────────────────────

interface UiBlockInput {
  rooms: number | '';
  occupants: number | '';
  persons: number | '';
  beds: number | '';
  kitchen: number | '';
  tubs: number | '';
}

function emptyBlock(): UiBlockInput {
  return { rooms: '', occupants: '', persons: '', beds: '', kitchen: 0, tubs: 0 };
}

// ─── Page ───────────────────────────────────────────────────────────────────

export function CommercialHeatPumpCalculatorPage() {
  const navigate = useNavigate();
  const [session, setSession] = useState(() => readStoredSession());

  // Form state
  const [climate, setClimate] = useState<ClimateKey | ''>('');
  const [ptype, setPtype] = useState<PropertyKey | ''>('');
  const [ctype, setCtype] = useState<ConstructionUiKey | ''>('');
  const [blockCount, setBlockCount] = useState<number | ''>('');
  const [singleBlock, setSingleBlock] = useState<UiBlockInput>(emptyBlock());
  const [blockInputs, setBlockInputs] = useState<UiBlockInput[]>([]);
  const [quickFill, setQuickFill] = useState<{ rooms: number | ''; kitchen: number | ''; tubs: number | '' }>(
    { rooms: '', kitchen: '', tubs: '' }
  );
  const [usage, setUsage] = useState<Usage | ''>('');
  const [reverseLine, setReverseLine] = useState<YN | ''>('');

  // Result state
  const [result, setResult] = useState<(CommercialHpResult & { roi?: RoiComparison; site_cop?: number }) | null>(null);
  // Per-block engine echoes are NOT in the wire response; we render the single-
  // shape recommendation when in single mode and a stub when distributed. The
  // backend route currently aggregates blockwise into ONE budget_config /
  // premium_config block — that's what we display.
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // ROI panel toggle (default collapsed per reference)
  const [showRoi, setShowRoi] = useState(false);

  // Lead capture (visual parity)
  const [leadName, setLeadName] = useState('');
  const [leadPhone, setLeadPhone] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [leadCity, setLeadCity] = useState('');
  const [leadNotes, setLeadNotes] = useState('');
  const [savedQuotationId, setSavedQuotationId] = useState<string | null>(null);
  const [savingQuote, setSavingQuote] = useState(false);
  const [leadSent, setLeadSent] = useState(false);
  const [sendingLead, setSendingLead] = useState(false);

  const distributed = ctype === 'block-wise' || ctype === 'independent' || ctype === 'cluster';
  const isCluster = ctype === 'cluster';

  // ── Sync block array length to blockCount ──────────────────────────────────
  function ensureBlockCount(n: number) {
    setBlockInputs((prev) => {
      const next: UiBlockInput[] = [];
      for (let i = 0; i < n; i++) {
        next.push(prev[i] ?? emptyBlock());
      }
      return next;
    });
  }

  function handleBlockCountChange(v: string) {
    const n = v === '' ? '' : Math.max(0, Math.min(30, Number(v)));
    setBlockCount(n);
    if (typeof n === 'number' && n >= 2) {
      ensureBlockCount(n);
    } else {
      setBlockInputs([]);
    }
  }

  // ── Distribute quick-fill: floor-divide totals across blocks ───────────────
  function handleDistribute() {
    if (!distributed || typeof blockCount !== 'number' || blockCount < 2) return;
    const totalR = typeof quickFill.rooms === 'number' ? quickFill.rooms : 0;
    const totalK = typeof quickFill.kitchen === 'number' ? quickFill.kitchen : 0;
    const totalT = typeof quickFill.tubs === 'number' ? quickFill.tubs : 0;
    const dist = (total: number, n: number): number[] => {
      const base = Math.floor(total / n);
      const rem = total - base * n;
      return Array.from({ length: n }, (_, i) => (i < rem ? base + 1 : base));
    };
    const rA = dist(totalR, blockCount);
    const kA = dist(totalK, blockCount);
    const tA = dist(totalT, blockCount);
    setBlockInputs((prev) =>
      prev.map((b, i) => ({
        ...b,
        rooms: totalR > 0 ? rA[i] : b.rooms,
        kitchen: kA[i],
        tubs: tA[i],
      }))
    );
  }

  function patchSingleBlock(p: Partial<UiBlockInput>) {
    setSingleBlock((b) => ({ ...b, ...p }));
  }

  function patchBlock(i: number, p: Partial<UiBlockInput>) {
    setBlockInputs((prev) => prev.map((b, idx) => (idx === i ? { ...b, ...p } : b)));
  }

  // ── Validation gate for Calculate button ───────────────────────────────────
  const allFilled = useMemo(() => {
    if (!climate || !ptype || !ctype || !usage || !reverseLine) return false;
    const pm = PROPERTY_META[ptype as PropertyKey];
    const blocks = distributed ? blockInputs : [singleBlock];
    if (distributed && (typeof blockCount !== 'number' || blockCount < 2)) return false;
    if (blocks.length === 0) return false;
    for (const b of blocks) {
      if (pm.personBased) {
        if (typeof b.persons !== 'number' || b.persons <= 0) return false;
      } else if (pm.hospital) {
        if (typeof b.beds !== 'number' || b.beds <= 0) return false;
      } else if (pm.bedBased) {
        if (typeof b.rooms !== 'number' || b.rooms <= 0) return false;
        if (typeof b.occupants !== 'number' || b.occupants <= 0) return false;
      } else {
        if (typeof b.rooms !== 'number' || b.rooms <= 0) return false;
      }
    }
    return true;
  }, [climate, ptype, ctype, usage, reverseLine, distributed, blockInputs, singleBlock, blockCount]);

  if (!session) {
    return (
      <div className="mx-auto max-w-2xl p-6 mt-10 text-center">
        <AlertTriangle size={32} className="mx-auto mb-3 text-amber-600" />
        <h2 className="text-lg font-semibold text-slate-900">No active meeting session</h2>
        <p className="mt-1 text-slate-600">
          The Commercial Heat Pump calculator is only available inside a Meeting
          Mode session. Start one to bind the sizing to a lead.
        </p>
        <button
          type="button"
          onClick={() => navigate('/sales/meeting/start')}
          className="mt-4 rounded-md bg-slate-900 px-5 py-2 text-white hover:bg-slate-800"
        >
          Initiate Meeting Mode
        </button>
      </div>
    );
  }

  // Narrow session for async closures
  const sess = session;

  async function refreshSession() {
    const fresh = await commercialHpApi.startSession(sess.leadId);
    const updated = {
      token: fresh.token,
      sessionId: fresh.sessionId,
      expiresAt: fresh.expiresAt,
      leadId: sess.leadId,
      leadLabel: sess.leadLabel,
    };
    writeStoredSession(updated);
    setSession(updated);
    return updated;
  }

  function buildWireBlock(b: UiBlockInput): CommercialHpBlock {
    if (!ptype) throw new RangeError('Pick a property type first.');
    const pm = PROPERTY_META[ptype as PropertyKey];
    const block: CommercialHpBlock = {
      kitchen_pax: typeof b.kitchen === 'number' ? b.kitchen : 0,
      tubs: typeof b.tubs === 'number' ? b.tubs : 0,
    };
    if (pm.personBased) {
      block.persons = typeof b.persons === 'number' ? b.persons : 0;
    } else if (pm.hospital) {
      block.beds = typeof b.beds === 'number' ? b.beds : 0;
    } else if (pm.bedBased) {
      block.rooms = typeof b.rooms === 'number' ? b.rooms : 0;
      block.occupants_per_room =
        typeof b.occupants === 'number' && typeof b.rooms === 'number' && b.rooms > 0
          ? b.occupants / b.rooms
          : 0;
    } else {
      block.rooms = typeof b.rooms === 'number' ? b.rooms : 0;
      // Hotel-class default occupancy from reference HTML (ROOM_TO_GUEST_MULTIPLIER = 1.8).
      block.occupants_per_room = 1.8;
    }
    return block;
  }

  function buildBackendInput(): CommercialHpInput {
    if (!climate || !ptype || !ctype || !usage || !reverseLine) {
      throw new RangeError('Please complete all selections before calculating.');
    }
    const blocks = distributed
      ? blockInputs.map(buildWireBlock)
      : [buildWireBlock(singleBlock)];
    return {
      climate_zone: climate as unknown as CommercialHpInput['climate_zone'],
      property_type: ptype as unknown as CommercialHpInput['property_type'],
      construction_type: distributed ? 'blockwise' : 'single',
      blocks,
      usage_intensity: usage as Usage,
      reverse_line: reverseLine === 'yes',
    };
  }

  async function handleCalculate() {
    setError(null);
    setLoading(true);
    setSavedQuotationId(null);
    setLeadSent(false);
    try {
      const input = buildBackendInput();
      let res: CommercialHpResult & { roi?: RoiComparison; site_cop?: number };
      try {
        res = (await commercialHpApi.calculate(
          input,
          sess.token
        )) as CommercialHpResult & { roi?: RoiComparison; site_cop?: number };
      } catch (e: any) {
        if (e?.response?.status === 403) {
          const fresh = await refreshSession();
          res = (await commercialHpApi.calculate(
            input,
            fresh.token
          )) as CommercialHpResult & { roi?: RoiComparison; site_cop?: number };
        } else {
          throw e;
        }
      }
      setResult(res);
      window.setTimeout(() => {
        document.getElementById('chp-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    } catch (e: any) {
      setError(
        e?.response?.data?.details?.message ||
          e?.response?.data?.error ||
          e?.message ||
          'Calculation failed. Please review the inputs.'
      );
    } finally {
      setLoading(false);
    }
  }

  function handleReset() {
    setClimate('');
    setPtype('');
    setCtype('');
    setBlockCount('');
    setBlockInputs([]);
    setQuickFill({ rooms: '', kitchen: '', tubs: '' });
    setSingleBlock(emptyBlock());
    setUsage('');
    setReverseLine('');
    setResult(null);
    setError(null);
    setShowRoi(false);
  }

  async function handleSaveQuotation(): Promise<string | null> {
    if (!result) return null;
    setSavingQuote(true);
    setError(null);
    try {
      const input = buildBackendInput();
      const out = await commercialHpApi.saveQuotation(input, result, sess.token);
      setSavedQuotationId(out.id);
      return out.id;
    } catch (e: any) {
      setError(
        e?.response?.data?.details?.message ||
          e?.response?.data?.error ||
          e?.message ||
          'Could not save quotation.'
      );
      return null;
    } finally {
      setSavingQuote(false);
    }
  }

  async function handleSendLead() {
    if (!leadName.trim() || !leadPhone.trim()) {
      setError('Please enter your name and mobile number so the Sunstellar team can reach you.');
      return;
    }
    setSendingLead(true);
    setError(null);
    try {
      if (!savedQuotationId && result) await handleSaveQuotation();
      setLeadSent(true);
    } catch (e: any) {
      setError(
        e?.response?.data?.details?.message ||
          e?.response?.data?.error ||
          e?.message ||
          'Could not send enquiry. Please retry.'
      );
    } finally {
      setSendingLead(false);
    }
  }

  function handlePrint() {
    if (!result) {
      setError('Please calculate a recommendation first.');
      return;
    }
    window.print();
  }

  const showEviBanner = climate === 'extreme-cold';
  const showHospitalBanner = ptype === 'hospital';
  const pm = ptype ? PROPERTY_META[ptype as PropertyKey] : null;

  return (
    <div className="min-h-screen bg-stone-50 px-4 py-6 print:bg-white print:p-0">
      <div className="mx-auto max-w-3xl">
        {/* Back link */}
        <div className="mb-3 flex items-center gap-2 print:hidden">
          <button
            type="button"
            onClick={() => navigate('/sales/meeting/calculators')}
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          >
            <ArrowLeft size={14} /> Choose another calculator
          </button>
        </div>

        {/* Header */}
        <div className="mb-4 rounded-lg bg-white px-6 py-5 shadow-sm">
          <h1 className="text-lg font-semibold text-slate-900">
            Sunstellar Hot Water Calculator
          </h1>
          <p className="mt-1 text-xs text-slate-500">
            Commercial heat pump sizing across hospitality, hospital, hostel and barrack properties
            {session.leadLabel ? ` · ${session.leadLabel}` : ''}
          </p>
        </div>

        {/* STEP 1 — Property basics */}
        <section className="mb-4 rounded-lg bg-white px-6 py-5 shadow-sm print:hidden" data-testid="chp-step-property">
          <SectionHeading n={1} title="Property basics" />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Location / Climate Zone *">
              <select
                value={climate}
                onChange={(e) => setClimate(e.target.value as ClimateKey | '')}
                data-testid="chp-climate"
                className={SELECT_CLASS}
              >
                <option value="">— Select climate zone —</option>
                {(Object.keys(CLIMATE_META) as ClimateKey[]).map((k) => (
                  <option key={k} value={k}>
                    {CLIMATE_META[k].label} ({CLIMATE_META[k].cityHint})
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Property type *">
              <select
                value={ptype}
                onChange={(e) => {
                  setPtype(e.target.value as PropertyKey | '');
                  setSingleBlock(emptyBlock());
                  setBlockInputs((prev) => prev.map(() => emptyBlock()));
                }}
                data-testid="chp-ptype"
                className={SELECT_CLASS}
              >
                <option value="">Select type</option>
                {(Object.keys(PROPERTY_META) as PropertyKey[]).map((k) => (
                  <option key={k} value={k}>
                    {PROPERTY_META[k].label}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {showEviBanner && (
            <div
              className="mt-3 rounded-lg border border-blue-300 bg-blue-50 px-4 py-3 text-xs leading-relaxed text-blue-900"
              data-testid="chp-evi-banner"
            >
              <strong>❄ EVI series auto-selected for sub-zero deployment.</strong>{' '}
              Standard ST-HP rated only down to −7°C. EVI machines work to −30°C.
            </div>
          )}
          {showHospitalBanner && (
            <div
              className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-900"
              data-testid="chp-hospital-banner"
            >
              <strong>🏥 Hospital sizing rules:</strong> 40 L/bed/day. NABH/IPC
              compliance needs a 5–10 kW electric booster for 70°C+ Legionella
              disinfection.
            </div>
          )}
        </section>

        {/* STEP 2 — Construction layout */}
        <section className="mb-4 rounded-lg bg-white px-6 py-5 shadow-sm print:hidden" data-testid="chp-step-construction">
          <SectionHeading n={2} title="Construction layout" />
          <Field label="Construction type *">
            <select
              value={ctype}
              onChange={(e) => {
                const v = e.target.value as ConstructionUiKey | '';
                setCtype(v);
                if (v === 'block-wise' || v === 'independent' || v === 'cluster') {
                  if (typeof blockCount !== 'number' || blockCount < 2) {
                    setBlockCount('');
                    setBlockInputs([]);
                  }
                } else if (v === 'single') {
                  setBlockCount('');
                  setBlockInputs([]);
                }
              }}
              data-testid="chp-ctype"
              className={SELECT_CLASS}
            >
              <option value="">Select type</option>
              <option value="single">Single building</option>
              <option value="block-wise">Block-wise</option>
              <option value="independent">Independent rooms spread across</option>
              <option value="cluster">Cluster</option>
            </select>
          </Field>
          {distributed && (
            <>
              <div className="mt-3">
                <Field label={`No. of ${isCluster ? 'clusters' : 'blocks'} *`}>
                  <input
                    type="number"
                    min={2}
                    max={30}
                    step={1}
                    value={blockCount}
                    onChange={(e) => handleBlockCountChange(e.target.value)}
                    placeholder="e.g. 3"
                    className={INPUT_CLASS + ' max-w-[160px]'}
                    data-testid="chp-block-count"
                  />
                </Field>
              </div>
              <div
                className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-900"
                data-testid="chp-distributed-banner"
              >
                <strong>Distributed sizing:</strong> Each {isCluster ? 'cluster' : 'block'} gets its own heat pump + tank.
              </div>
            </>
          )}
        </section>

        {/* STEP 3 — Capacity */}
        {ctype && pm && (
          <section className="mb-4 rounded-lg bg-white px-6 py-5 shadow-sm print:hidden" data-testid="chp-step-capacity">
            <SectionHeading n={3} title={distributed ? `Per-${isCluster ? 'cluster' : 'block'} details` : 'Capacity'} />

            {!distributed && (
              <SingleCapacityFields
                pm={pm}
                block={singleBlock}
                onChange={patchSingleBlock}
              />
            )}

            {distributed && typeof blockCount === 'number' && blockCount >= 2 && (
              <>
                {/* Quick fill row */}
                <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-4 py-3">
                  <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-blue-900">
                    Distribute quick-fill
                  </div>
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    <Field label="Total rooms" small>
                      <input
                        type="number"
                        min={0}
                        step={1}
                        placeholder="e.g. 60"
                        value={quickFill.rooms}
                        onChange={(e) =>
                          setQuickFill((q) => ({
                            ...q,
                            rooms: e.target.value === '' ? '' : Number(e.target.value),
                          }))
                        }
                        className={INPUT_CLASS}
                        data-testid="chp-quick-rooms"
                      />
                    </Field>
                    <Field label="Total kitchen pax" small>
                      <input
                        type="number"
                        min={0}
                        step={10}
                        placeholder="0"
                        value={quickFill.kitchen}
                        onChange={(e) =>
                          setQuickFill((q) => ({
                            ...q,
                            kitchen: e.target.value === '' ? '' : Number(e.target.value),
                          }))
                        }
                        className={INPUT_CLASS}
                        data-testid="chp-quick-kitchen"
                      />
                    </Field>
                    <Field label="Total tubs" small>
                      <input
                        type="number"
                        min={0}
                        step={1}
                        placeholder="0"
                        value={quickFill.tubs}
                        onChange={(e) =>
                          setQuickFill((q) => ({
                            ...q,
                            tubs: e.target.value === '' ? '' : Number(e.target.value),
                          }))
                        }
                        className={INPUT_CLASS}
                        data-testid="chp-quick-tubs"
                      />
                    </Field>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={handleDistribute}
                        className="w-full rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
                        data-testid="chp-quick-distribute"
                      >
                        Distribute →
                      </button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {blockInputs.map((b, i) => (
                    <div
                      key={i}
                      className="rounded-md border border-slate-200 bg-slate-50 p-3"
                      data-testid={`chp-block-${i}`}
                    >
                      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                        {isCluster ? `Cluster ${i + 1}` : `Block ${i + 1}`}
                      </div>
                      <BlockFields
                        pm={pm}
                        block={b}
                        onChange={(p) => patchBlock(i, p)}
                        idx={i}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
        )}

        {/* STEP 4 — Usage & delivery */}
        {ctype && (
          <section className="mb-4 rounded-lg bg-white px-6 py-5 shadow-sm print:hidden" data-testid="chp-step-usage">
            <SectionHeading n={4} title="Usage & delivery" />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label="Usage intensity *">
                <select
                  value={usage}
                  onChange={(e) => setUsage(e.target.value as Usage | '')}
                  data-testid="chp-usage"
                  className={SELECT_CLASS}
                >
                  <option value="">Select</option>
                  <option value="low">Low — bucket type</option>
                  <option value="moderate">Moderate — standard showers</option>
                  <option value="high">High — body jets / rain showers</option>
                </select>
              </Field>
              <Field label="Reverse line *">
                <select
                  value={reverseLine}
                  onChange={(e) => setReverseLine(e.target.value as YN | '')}
                  data-testid="chp-reverse"
                  className={SELECT_CLASS}
                >
                  <option value="">Select</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </Field>
            </div>
          </section>
        )}

        {error && (
          <div
            role="alert"
            className="mb-3 rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-800"
            data-testid="chp-error"
          >
            {error}
          </div>
        )}

        {/* Action row */}
        {ctype && (
          <div className="mb-6 flex flex-wrap gap-2 print:hidden">
            <button
              type="button"
              onClick={handleCalculate}
              disabled={!allFilled || loading}
              className="flex-1 min-w-[200px] rounded-md bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
              data-testid="chp-calculate"
            >
              {loading ? 'Calculating…' : 'Calculate & get recommendation'}
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="rounded-md border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
              data-testid="chp-reset"
            >
              Reset
            </button>
          </div>
        )}

        {/* Result panel */}
        {result && (
          <div id="chp-results" data-testid="chp-results" className="space-y-4">
            <h2 className="text-base font-semibold text-slate-900">Whole-property load</h2>
            <KpiStrip
              tiles={[
                { label: 'Total LPD', value: Math.round(result.total_lpd).toLocaleString('en-IN'), unit: 'L/day' },
                { label: 'Heating load', value: result.heating_kw.toFixed(1), unit: 'kW' },
                {
                  label: 'Climate COP',
                  value: (
                    result.site_cop ?? SITE_COP_BY_CLIMATE[(climate || 'composite') as ClimateKey]
                  ).toFixed(2),
                },
                {
                  label: 'Series',
                  value: showEviBanner
                    ? 'EVI'
                    : result.heating_kw < 15
                      ? 'Domestic'
                      : 'Commercial',
                },
              ]}
            />

            <RecommendationCards
              result={result}
              showEvi={showEviBanner}
              isHospital={!!showHospitalBanner}
              ptype={ptype as PropertyKey}
              isPremium={!!pm?.premium}
            />

            {result.engineering_notes && result.engineering_notes.length > 0 && (
              <div className="rounded-lg border border-slate-200 bg-stone-50 px-4 py-3">
                <div className="mb-1 text-sm font-semibold text-slate-900">Engineering notes</div>
                <ul className="ml-4 list-disc text-xs leading-relaxed text-slate-700">
                  {result.engineering_notes.map((n, i) => (
                    <li key={i} className="mb-1">
                      {n}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* ROI section — collapsed by default */}
            <div className="rounded-lg border-t-2 border-slate-200 bg-white pt-4 print:hidden">
              <div className="flex flex-wrap items-center justify-between gap-2 px-1 pb-3">
                <h3 className="text-base font-semibold text-slate-900">
                  ROI Analysis · Heat Pump vs Boilers
                </h3>
                <button
                  type="button"
                  onClick={() => setShowRoi((v) => !v)}
                  className="rounded-md bg-slate-900 px-4 py-1.5 text-xs font-medium text-white hover:bg-slate-800"
                  data-testid="chp-roi-toggle"
                >
                  {showRoi ? 'Hide ROI' : 'Show ROI'}
                </button>
              </div>
              {showRoi && (
                <div data-testid="chp-roi-body" className="px-1">
                  {result.roi ? (
                    <RoiPanel data={adaptRoi(result.roi, result.heating_kw, result.total_lpd)} />
                  ) : (
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                      ROI comparison not available — backend did not return the TCO block.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Lead form */}
            <div className="rounded-lg bg-white p-5 shadow-sm print:hidden" data-testid="chp-lead">
              <h3 className="text-sm font-semibold text-slate-900">Get a detailed quote</h3>
              <p className="mt-1 text-xs text-slate-600">
                Share your details and our team will send you a customised quote based on this recommendation.
              </p>
              <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                <LeadField label="Full name *" value={leadName} onChange={setLeadName} placeholder="Your name" testId="chp-lead-name" />
                <LeadField label="Mobile number *" value={leadPhone} onChange={setLeadPhone} placeholder="+91 98XXXXXXXX" type="tel" testId="chp-lead-phone" />
                <LeadField label="Email address" value={leadEmail} onChange={setLeadEmail} placeholder="you@example.com" type="email" />
                <LeadField label="City" value={leadCity} onChange={setLeadCity} placeholder="Delhi, Mumbai…" />
              </div>
              <div className="mt-2.5">
                <LeadField label="Any specific requirement or question?" value={leadNotes} onChange={setLeadNotes} placeholder="Optional notes…" />
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleSendLead}
                  disabled={sendingLead || leadSent}
                  className="inline-flex flex-1 min-w-[180px] items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:bg-slate-400"
                  data-testid="chp-send-lead"
                >
                  <Send size={14} />
                  {leadSent ? 'Enquiry sent' : sendingLead ? 'Sending…' : 'Send enquiry →'}
                </button>
                <button
                  type="button"
                  onClick={handlePrint}
                  className="inline-flex flex-1 min-w-[180px] items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  data-testid="chp-print"
                >
                  <Printer size={14} /> Print / Save as PDF
                </button>
              </div>
              {leadSent && (
                <div
                  className="mt-3 rounded-md border-l-4 border-emerald-600 bg-emerald-50 px-4 py-2 text-sm text-emerald-800"
                  data-testid="chp-lead-confirm"
                >
                  ✓ Enquiry sent! Our team will contact you within 24 hours.
                </div>
              )}
            </div>

            {/* AURA — save & convert */}
            <div className="rounded-lg bg-white p-5 shadow-sm print:hidden">
              <div className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                AURA — save &amp; convert
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleSaveQuotation}
                  disabled={savingQuote || !!savedQuotationId}
                  className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                  data-testid="chp-save-quote"
                >
                  {savedQuotationId ? 'Quotation saved' : savingQuote ? 'Saving…' : 'Save Quotation'}
                </button>
                {savedQuotationId && (
                  <ConvertToProposalButton
                    calculatorKey="commercial_heat_pump"
                    quotationId={savedQuotationId}
                    customerName={session.leadLabel ?? undefined}
                    leadId={session.leadId ?? undefined}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 text-center text-xs text-slate-400 print:hidden">
          Sunstellar Commercial Heat Pump Calculator · v2.0 · Bapauli, Haryana · Noida · Delhi
        </div>
      </div>
      <span className="sr-only">{typeof proposalsApi}</span>
    </div>
  );
}

// ─── Helper components ──────────────────────────────────────────────────────

const SELECT_CLASS =
  'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200';
const INPUT_CLASS =
  'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200';

function SectionHeading({ n, title }: { n: number; title: string }) {
  return (
    <div className="mb-3 flex items-center gap-2.5">
      <span
        aria-hidden
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-900 text-[11px] font-semibold text-white"
      >
        {n}
      </span>
      <h2 className="text-base font-medium text-slate-900">{title}</h2>
    </div>
  );
}

function Field({
  label,
  children,
  small,
}: {
  label: string;
  children: React.ReactNode;
  small?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label
        className={
          small
            ? 'text-[11px] font-medium uppercase tracking-wide text-slate-500'
            : 'text-xs font-medium text-slate-600'
        }
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function LeadField({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  testId,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  testId?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-slate-600">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
        data-testid={testId}
      />
    </div>
  );
}

function SingleCapacityFields({
  pm,
  block,
  onChange,
}: {
  pm: PropertyMeta;
  block: UiBlockInput;
  onChange: (p: Partial<UiBlockInput>) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      {pm.personBased && (
        <Field label="No. of persons / beds *">
          <input
            type="number"
            min={1}
            step={1}
            value={block.persons}
            onChange={(e) =>
              onChange({ persons: e.target.value === '' ? '' : Number(e.target.value) })
            }
            className={INPUT_CLASS}
            data-testid="chp-single-persons"
          />
        </Field>
      )}
      {pm.hospital && (
        <Field label="No. of beds *">
          <input
            type="number"
            min={1}
            step={1}
            value={block.beds}
            onChange={(e) =>
              onChange({ beds: e.target.value === '' ? '' : Number(e.target.value) })
            }
            className={INPUT_CLASS}
            data-testid="chp-single-beds"
          />
        </Field>
      )}
      {!pm.personBased && !pm.hospital && (
        <Field label="No. of rooms / keys *">
          <input
            type="number"
            min={1}
            step={1}
            value={block.rooms}
            onChange={(e) =>
              onChange({ rooms: e.target.value === '' ? '' : Number(e.target.value) })
            }
            className={INPUT_CLASS}
            data-testid="chp-single-rooms"
          />
        </Field>
      )}
      {pm.bedBased && (
        <Field label="Total occupants *">
          <input
            type="number"
            min={1}
            step={1}
            value={block.occupants}
            onChange={(e) =>
              onChange({ occupants: e.target.value === '' ? '' : Number(e.target.value) })
            }
            className={INPUT_CLASS}
            data-testid="chp-single-occupants"
          />
        </Field>
      )}
      <Field label="Kitchen / mess (No. of Persons)">
        <input
          type="number"
          min={0}
          step={10}
          placeholder="0 if none"
          value={block.kitchen}
          onChange={(e) =>
            onChange({ kitchen: e.target.value === '' ? 0 : Number(e.target.value) })
          }
          className={INPUT_CLASS}
          data-testid="chp-single-kitchen"
        />
      </Field>
      {!pm.hospital && !pm.personBased && (
        <Field label="Jacuzzi / bath tubs">
          <input
            type="number"
            min={0}
            step={1}
            value={block.tubs}
            onChange={(e) =>
              onChange({ tubs: e.target.value === '' ? 0 : Number(e.target.value) })
            }
            className={INPUT_CLASS}
            data-testid="chp-single-tubs"
          />
        </Field>
      )}
    </div>
  );
}

function BlockFields({
  pm,
  block,
  onChange,
  idx,
}: {
  pm: PropertyMeta;
  block: UiBlockInput;
  onChange: (p: Partial<UiBlockInput>) => void;
  idx: number;
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {pm.personBased && (
        <Field label="Persons / beds *" small>
          <input
            type="number"
            min={0}
            step={1}
            value={block.persons}
            onChange={(e) =>
              onChange({ persons: e.target.value === '' ? '' : Number(e.target.value) })
            }
            className={INPUT_CLASS}
            data-testid={`chp-block-${idx}-persons`}
          />
        </Field>
      )}
      {pm.hospital && (
        <Field label="Beds *" small>
          <input
            type="number"
            min={0}
            step={1}
            value={block.beds}
            onChange={(e) =>
              onChange({ beds: e.target.value === '' ? '' : Number(e.target.value) })
            }
            className={INPUT_CLASS}
            data-testid={`chp-block-${idx}-beds`}
          />
        </Field>
      )}
      {!pm.personBased && !pm.hospital && (
        <Field label="Rooms *" small>
          <input
            type="number"
            min={0}
            step={1}
            value={block.rooms}
            onChange={(e) =>
              onChange({ rooms: e.target.value === '' ? '' : Number(e.target.value) })
            }
            className={INPUT_CLASS}
            data-testid={`chp-block-${idx}-rooms`}
          />
        </Field>
      )}
      {pm.bedBased && (
        <Field label="Occupants *" small>
          <input
            type="number"
            min={0}
            step={1}
            value={block.occupants}
            onChange={(e) =>
              onChange({ occupants: e.target.value === '' ? '' : Number(e.target.value) })
            }
            className={INPUT_CLASS}
            data-testid={`chp-block-${idx}-occupants`}
          />
        </Field>
      )}
      <Field label="Kitchen pax" small>
        <input
          type="number"
          min={0}
          step={10}
          value={block.kitchen}
          onChange={(e) =>
            onChange({ kitchen: e.target.value === '' ? 0 : Number(e.target.value) })
          }
          className={INPUT_CLASS}
          data-testid={`chp-block-${idx}-kitchen`}
        />
      </Field>
      {!pm.hospital && !pm.personBased && (
        <Field label="Tubs" small>
          <input
            type="number"
            min={0}
            step={1}
            value={block.tubs}
            onChange={(e) =>
              onChange({ tubs: e.target.value === '' ? 0 : Number(e.target.value) })
            }
            className={INPUT_CLASS}
            data-testid={`chp-block-${idx}-tubs`}
          />
        </Field>
      )}
    </div>
  );
}

// ─── Recommendation cards (Budget + Premium per block-or-single) ────────────

function RecommendationCards({
  result,
  showEvi,
  isHospital,
  ptype,
  isPremium,
}: {
  result: CommercialHpResult;
  showEvi: boolean;
  isHospital: boolean;
  ptype: PropertyKey;
  isPremium: boolean;
}) {
  void ptype;
  const isDomestic = result.heating_kw < 15;
  const classLabel = isDomestic ? 'domestic class' : showEvi ? 'EVI commercial' : 'commercial class';
  const classColour = isDomestic ? 'text-emerald-700' : 'text-blue-800';

  // Approximate daily kWh — assume site COP ≈ 4.0 if not echoed. Reference
  // uses model COP per SKU; without that detail we estimate from heating_kw.
  const dailyKWhEstimate = Math.round((result.heating_kw * HEATING_WINDOW_HOURS) / 4.0);

  return (
    <div data-testid="chp-recommendation">
      <p className="mb-2 text-xs text-slate-600">
        Heating load <strong>{result.heating_kw.toFixed(1)} kW</strong> →{' '}
        <strong className={classColour}>{classLabel}</strong>
      </p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {/* Budget */}
        <ConfigCard
          variant="budget"
          title="Option 1 · Budget"
          rows={result.budget_config}
          loadKw={result.heating_kw}
          dailyKWh={dailyKWhEstimate}
          isDomestic={isDomestic}
          showEvi={showEvi}
        />
        {/* Premium — only when property is premium-eligible */}
        {isPremium ? (
          result.premium_config && result.premium_config.length > 0 ? (
            <ConfigCard
              variant="premium"
              title="Option 2 · Premium"
              rows={result.premium_config}
              loadKw={result.heating_kw}
              dailyKWh={dailyKWhEstimate}
              isDomestic={isDomestic}
              showEvi={showEvi}
            />
          ) : (
            <div
              className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900"
              data-testid="chp-premium-na"
            >
              <p className="text-sm font-medium">Premium configuration not available</p>
              <p className="mt-1 text-xs">
                No SKU satisfies the Premium constraints (total ≥ 120% load, per-unit ≥ 60% load,
                shared tank capacity). Suggest customer review Budget option or contact engineering.
              </p>
            </div>
          )
        ) : null}
      </div>
      {isHospital && (
        <p className="mt-3 text-xs italic text-slate-600">
          Hospital sizing requires a 5–10 kW electric booster for 70°C+ Legionella disinfection
          per NABH/IPC guidelines (not included in this configuration).
        </p>
      )}
    </div>
  );
}

function ConfigCard({
  variant,
  title,
  rows,
  loadKw,
  dailyKWh,
  isDomestic,
  showEvi,
}: {
  variant: 'budget' | 'premium';
  title: string;
  rows: CommercialHpResult['budget_config'];
  loadKw: number;
  dailyKWh: number;
  isDomestic: boolean;
  showEvi: boolean;
}) {
  const isPremium = variant === 'premium';
  const border = isPremium
    ? 'border-2 border-emerald-600'
    : 'border border-slate-200';
  const badgeBg = isPremium ? 'bg-emerald-50 text-emerald-800' : 'bg-slate-100 text-slate-700';

  if (!rows || rows.length === 0) {
    return (
      <div
        data-testid={`chp-config-${variant}`}
        className={`rounded-lg bg-white p-4 ${border}`}
      >
        <span className={`inline-block rounded px-2 py-0.5 text-[11px] font-semibold ${badgeBg}`}>
          {title}
        </span>
        <p className="mt-2 text-xs italic text-slate-500">
          No valid {isPremium ? 'Premium' : 'Budget'} config for this load.
        </p>
      </div>
    );
  }

  // Total installed kW + qty + overshoot %.
  const totalInstalledKw = rows.reduce((sum, r) => {
    // tank_l is per row; rated kW not echoed — derive from name when prefixed,
    // else assume each row covers its share of the load.
    const nameKw = Number((r.sku.match(/(\d+)/g) || []).slice(-1)[0]) || 0;
    return sum + nameKw * r.qty;
  }, 0);
  const overshootPct =
    loadKw > 0 ? Math.round(((totalInstalledKw - loadKw) / loadKw) * 100) : 0;
  const overshootStr = (overshootPct >= 0 ? '+' : '') + overshootPct + '%';

  return (
    <div
      data-testid={`chp-config-${variant}`}
      className={`rounded-lg bg-white p-4 ${border}`}
    >
      <div className="flex items-center gap-2">
        <span className={`inline-block rounded px-2 py-0.5 text-[11px] font-semibold ${badgeBg}`}>
          {title}
        </span>
        {isPremium && (
          <span className="rounded bg-emerald-600 px-2 py-0.5 text-[10px] font-semibold uppercase text-white">
            Recommended
          </span>
        )}
        {showEvi && (
          <span className="rounded bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-800">
            EVI
          </span>
        )}
        {isDomestic && !isPremium && (
          <span className="rounded bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-800">
            Domestic class
          </span>
        )}
      </div>
      <table className="mt-3 w-full text-xs">
        <thead>
          <tr className="border-b border-slate-200 text-left text-slate-500">
            <th className="py-1">SKU</th>
            <th className="py-1 text-right">Qty</th>
            <th className="py-1 text-right">Tank (L)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.sku}-${i}`} className="border-b border-slate-100 last:border-none">
              <td className="py-1 font-medium text-slate-900">{r.sku}</td>
              <td className="py-1 text-right">{r.qty}</td>
              <td className="py-1 text-right">{r.tank_l.toLocaleString('en-IN')}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] text-slate-600">
        <div>
          Daily power: <strong className="text-slate-900">{dailyKWh} kWh</strong>
        </div>
        <div className="text-right">
          Overshoot vs load: <strong className="text-slate-900">{overshootStr}</strong>
        </div>
      </div>
      {isPremium && (
        <p className="mt-2 rounded-md bg-emerald-50 px-3 py-2 text-[11px] leading-relaxed text-emerald-900">
          <strong>Variable-load operation:</strong> Off-peak hours run one machine
          alone (alternates weekly to balance wear). At peak demand, both machines
          run in parallel through the shared tank.
        </p>
      )}
    </div>
  );
}

// ─── ROI adapter: legacy RoiComparison wire shape → RoiPanel.RoiData ────────

function adaptRoi(roi: RoiComparison, loadKw: number, lpd: number): RoiData {
  void loadKw;
  void lpd;
  const mapTech = (
    t: RoiComparison['hp'],
    name: string,
    fallbackEff: string,
    fallbackDaily: string
  ) => ({
    name,
    effLabel: t.efficiency_label ?? fallbackEff,
    capex: t.capex,
    dailyEnergyUnits: t.daily_energy != null ? `${t.daily_energy.toFixed(1)} ${fallbackDaily}` : '—',
    annualFuel: t.annual_fuel ?? 0,
    annualMaint: t.annual_maint ?? 0,
    annualOpex: t.annual_opex,
    fiveYearTCO: t.tco_5y,
    tenYearTCO: t.tco_10y,
  });

  const hp = {
    ...mapTech(roi.hp, 'Heat Pump', 'COP 4.0', 'kWh elec'),
  };
  const elec = {
    ...mapTech(roi.electric, 'Electric Boiler', '95% efficient', 'kWh elec'),
    annualSavingsVsHP: roi.savings?.vs_electric.annual,
    capexPremiumVsHP: roi.savings?.vs_electric.capex_premium,
    paybackYears: roi.savings?.vs_electric.payback_years ?? null,
  };
  const diesel = {
    ...mapTech(roi.diesel, 'Diesel Boiler', '85% efficient', 'L/day'),
    annualSavingsVsHP: roi.savings?.vs_diesel.annual,
    capexPremiumVsHP: roi.savings?.vs_diesel.capex_premium,
    paybackYears: roi.savings?.vs_diesel.payback_years ?? null,
  };
  const gas = {
    ...mapTech(roi.gas, 'Gas Boiler', '90% efficient', 'kg/day'),
    annualSavingsVsHP: roi.savings?.vs_gas.annual,
    capexPremiumVsHP: roi.savings?.vs_gas.capex_premium,
    paybackYears: roi.savings?.vs_gas.payback_years ?? null,
  };

  // Cumulative cost series over 5 years (year 0..5 inclusive).
  function series(tech: { capex: number; annualOpex: number }): number[] {
    const arr: number[] = [];
    for (let y = 0; y <= 5; y++) {
      arr.push(tech.capex + tech.annualOpex * y);
    }
    return arr;
  }
  const cumulative = {
    hp: series(hp),
    elec: series(elec),
    diesel: series(diesel),
    gas: series(gas),
  };

  const notes = [
    'Routing: load < 15 kW → multi-unit domestic. ≥ 15 kW → commercial. Sub-zero → EVI.',
    'Budget: smallest qty meeting heating load × 0.90 floor (−10% tolerance), prefer single unit.',
    'Premium: minimum 2 machines. Total installed kW ≥ 120% of load (20% headroom). Each unit alone covers ≥ 60% of load (failover).',
    'Property benchmarks: Hotel 75 L · Resort 100 L · Wellness 120 L · Hospital 40 L/bed · Guest house 60 L · Hostel 45 L · PG 30 L · Barracks 20/30/50 L by tier.',
    'Fuel prices flat (no escalation). Actual savings will favour HP further as fossil fuel prices rise.',
  ];

  return { hp, elec, diesel, gas, cumulative, notes };
}

export default CommercialHeatPumpCalculatorPage;
